import { ListStandardizedInstrumentsByCategoryQuery } from "../graphql/queries.js";
// ---------------------------------------------------------------------------
// Web pattern: categoriesFilters variables
// ---------------------------------------------------------------------------
const CATEGORY_PAGE_SIZE = 20;
const MAX_CACHED_EDGES = 100;
function buildCategoryVars(categoryName, userId, type = "fungible_token") {
    return {
        input: {
            ...(userId ? { userId } : {}),
            categoriesFilters: [
                {
                    categories: [{ category: categoryName }],
                    type,
                    limit: CATEGORY_PAGE_SIZE,
                    offset: 0,
                },
            ],
        },
    };
}
function readCategories(apolloClient, vars) {
    try {
        return apolloClient.cache.readQuery({
            query: ListStandardizedInstrumentsByCategoryQuery,
            variables: vars,
        });
    }
    catch {
        return null;
    }
}
export function prependToFungibleCategory(apolloClient, categoryName, instrument, userId) {
    const vars = buildCategoryVars(categoryName, userId);
    const existing = readCategories(apolloClient, vars);
    if (!existing)
        return;
    const listQuery = existing.listStandardizedInstrumentsByCategory;
    const categories = listQuery?.standardizedFungibleTokensByCategory;
    if (!Array.isArray(categories) || categories.length === 0)
        return;
    const cat = categories[0];
    const existingEdges = cat.edges ?? [];
    if (existingEdges.some((e) => e.standardizedInstrumentId === instrument.standardizedInstrumentId))
        return;
    const merged = [instrument, ...existingEdges].slice(0, MAX_CACHED_EDGES);
    apolloClient.cache.writeQuery({
        query: ListStandardizedInstrumentsByCategoryQuery,
        variables: vars,
        data: {
            listStandardizedInstrumentsByCategory: {
                ...listQuery,
                standardizedFungibleTokensByCategory: [
                    {
                        ...cat,
                        edges: merged,
                    },
                    ...categories.slice(1),
                ],
            },
        },
    });
}
export function removeFromFungibleCategory(apolloClient, categoryName, standardizedInstrumentId, userId) {
    const vars = buildCategoryVars(categoryName, userId);
    const existing = readCategories(apolloClient, vars);
    if (!existing)
        return;
    const listQuery = existing.listStandardizedInstrumentsByCategory;
    const categories = listQuery?.standardizedFungibleTokensByCategory;
    if (!Array.isArray(categories) || categories.length === 0)
        return;
    const cat = categories[0];
    const existingEdges = cat.edges ?? [];
    const filtered = existingEdges.filter((e) => e.standardizedInstrumentId !== standardizedInstrumentId);
    if (filtered.length === existingEdges.length)
        return;
    apolloClient.cache.writeQuery({
        query: ListStandardizedInstrumentsByCategoryQuery,
        variables: vars,
        data: {
            listStandardizedInstrumentsByCategory: {
                ...listQuery,
                standardizedFungibleTokensByCategory: [
                    {
                        ...cat,
                        edges: filtered,
                    },
                    ...categories.slice(1),
                ],
            },
        },
    });
}
function readLegacyCategories(apolloClient, vars) {
    try {
        return apolloClient.cache.readQuery({
            query: ListStandardizedInstrumentsByCategoryQuery,
            variables: vars,
        });
    }
    catch {
        return null;
    }
}
export function prependToFungibleCategoryLegacy(apolloClient, instrument) {
    const newestVars = {
        input: { category: "newest", offset: 0, limit: 100, instrumentType: "FUNGIBLE_TOKEN" },
    };
    // Mirror the non-legacy helpers: Apollo's `cache.readQuery` throws when the
    // query/variables pair isn't in the cache yet. Without this guard a missing
    // cache entry would propagate up to `LiveUpdatesProvider`'s subscription
    // handler.
    const existing = readLegacyCategories(apolloClient, newestVars);
    if (!existing?.listStandardizedInstrumentsByCategory?.hotTokens)
        return;
    const existingEdges = existing.listStandardizedInstrumentsByCategory.hotTokens.edges ?? [];
    if (existingEdges.some((e) => e.id === instrument.standardizedInstrumentId))
        return;
    const newEdge = {
        __typename: "StandardizedInstrument",
        id: instrument.standardizedInstrumentId,
        type: "FUNGIBLE_TOKEN",
        instrument,
    };
    apolloClient.cache.writeQuery({
        query: ListStandardizedInstrumentsByCategoryQuery,
        variables: newestVars,
        data: {
            listStandardizedInstrumentsByCategory: {
                ...existing.listStandardizedInstrumentsByCategory,
                hotTokens: {
                    ...existing.listStandardizedInstrumentsByCategory.hotTokens,
                    edges: [newEdge, ...existingEdges],
                },
            },
        },
    });
}
